import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { stockKey } from "../hooks/useInventory";
import { formatPrice } from "../hooks/useCatalog";
import { SchoolLogoOption, EMPTY_LOGO } from "./SchoolLogoOption";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_SIZES = ["S", "M", "L", "XL"];

const inputCls =
  "w-full bg-white border border-ink/15 px-4 py-3 text-sm text-ink placeholder:text-mutedink/60 focus:outline-none focus:border-oxblood transition-colors duration-300";
const labelCls =
  "block font-mono text-[10px] uppercase tracking-[0.25em] text-mutedink mb-2";
const pillBase =
  "min-w-[3rem] px-4 py-2 border font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-300";
const pill = (active) =>
  `${pillBase} ${active ? "border-ink bg-ink text-parchment" : "border-ink/20 text-ink hover:border-oxblood"}`;

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  address_line1: "",
  address_line2: "",
  city: "",
  province: "",
  postal_code: "",
  country: "Canada",
};

const Field = ({ id, label, required, ...rest }) => (
  <div>
    <label htmlFor={id} className={labelCls}>
      {label}
      {required && " *"}
    </label>
    <input
      id={id}
      data-testid={`buy-${id}-input`}
      required={required}
      className={inputCls}
      {...rest}
    />
  </div>
);

const Options = ({
  product,
  stock,
  size,
  setSize,
  colour,
  setColour,
  qty,
  setQty,
  onNext,
}) => {
  const available = (s) =>
    stock ? (stock.get(stockKey(product.id, s, colour)) ?? 0) : 10;
  const maxQty = size ? Math.min(10, available(size)) : 10;
  return (
    <>
      <div className="mt-6">
        <p className={labelCls}>Size</p>
        <div className="flex flex-wrap gap-2">
          {(product.sizes || DEFAULT_SIZES).map((s) => {
            const soldOut = available(s) <= 0;
            return (
              <button
                key={s}
                type="button"
                data-testid={`buy-size-${s}`}
                disabled={soldOut}
                aria-disabled={soldOut}
                onClick={() => setSize(s)}
                className={`${pill(size === s)} disabled:opacity-40 disabled:line-through disabled:cursor-not-allowed`}
              >
                {s}
              </button>
            );
          })}
        </div>
        {stock &&
          (product.sizes || DEFAULT_SIZES).every((s) => available(s) <= 0) && (
            <p
              data-testid="buy-sold-out-msg"
              className="mt-3 text-xs text-oxblood"
            >
              Sold out in every size right now — check back soon.
            </p>
          )}
      </div>
      {product.colours.length > 1 && (
        <div className="mt-5">
          <p className={labelCls}>Colour</p>
          <div className="flex flex-wrap gap-2">
            {product.colours.map((c) => (
              <button
                key={c}
                type="button"
                data-testid={`buy-colour-${c.toLowerCase()}`}
                onClick={() => {
                  setColour(c);
                  setSize("");
                  setQty(1);
                }}
                className={pill(colour === c)}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="mt-5">
        <p className={labelCls}>Quantity</p>
        <div className="inline-flex border border-ink/20">
          <button
            type="button"
            data-testid="buy-qty-minus"
            onClick={() => setQty(Math.max(1, qty - 1))}
            className="px-4 py-2 text-ink hover:text-oxblood"
          >
            −
          </button>
          <span
            data-testid="buy-qty"
            className="px-4 py-2 font-mono text-sm text-ink border-x border-ink/20"
          >
            {qty}
          </span>
          <button
            type="button"
            data-testid="buy-qty-plus"
            disabled={qty >= maxQty}
            onClick={() => setQty(Math.min(maxQty, qty + 1))}
            className="px-4 py-2 text-ink hover:text-oxblood disabled:opacity-40"
          >
            +
          </button>
        </div>
        {size && stock && available(size) <= 3 && (
          <p data-testid="buy-low-stock" className="mt-2 text-xs text-mutedink">
            Only {available(size)} left in {size}.
          </p>
        )}
      </div>
      <button
        type="button"
        data-testid="buy-continue-btn"
        disabled={!size}
        onClick={onNext}
        className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-ink text-parchment py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-oxblood transition-colors duration-300 disabled:opacity-40 disabled:hover:bg-ink"
      >
        Continue — ${formatPrice(product.price * qty)} CAD
        <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </>
  );
};

const Details = ({
  form,
  set,
  sending,
  total,
  onBack,
  onSubmit,
  logo,
  setLogo,
}) => (
  <form
    onSubmit={onSubmit}
    data-testid="buy-details-form"
    className="mt-6 space-y-4"
  >
    <div className="grid sm:grid-cols-2 gap-4">
      <Field
        id="name"
        label="Full Name"
        required
        value={form.name}
        onChange={set("name")}
        placeholder="Ava Sinclair"
      />
      <Field
        id="email"
        label="Email"
        type="email"
        required
        value={form.email}
        onChange={set("email")}
        placeholder="ava@email.com"
      />
    </div>
    <Field
      id="phone"
      label="Phone"
      value={form.phone}
      onChange={set("phone")}
      placeholder="+1 604 555 0100"
    />
    <Field
      id="address_line1"
      label="Address"
      required
      value={form.address_line1}
      onChange={set("address_line1")}
      placeholder="123 Main Street"
    />
    <Field
      id="address_line2"
      label="Apt / Unit"
      value={form.address_line2}
      onChange={set("address_line2")}
      placeholder="Unit 4"
    />
    <div className="grid sm:grid-cols-3 gap-4">
      <Field
        id="city"
        label="City"
        required
        value={form.city}
        onChange={set("city")}
        placeholder="Vancouver"
      />
      <Field
        id="province"
        label="Province"
        required
        value={form.province}
        onChange={set("province")}
        placeholder="BC"
      />
      <Field
        id="postal_code"
        label="Postal Code"
        required
        value={form.postal_code}
        onChange={set("postal_code")}
        placeholder="V6B 1A1"
      />
    </div>
    <Field
      id="country"
      label="Country"
      required
      value={form.country}
      onChange={set("country")}
    />
    {logo && <SchoolLogoOption logo={logo} setLogo={setLogo} />}
    <div className="flex gap-3 pt-2">
      <button
        type="button"
        data-testid="buy-back-btn"
        onClick={onBack}
        className="px-5 py-3.5 border border-ink/20 font-mono text-[11px] uppercase tracking-[0.2em] text-ink hover:border-oxblood hover:text-oxblood transition-colors duration-300"
      >
        Back
      </button>
      <button
        type="submit"
        data-testid="buy-submit-btn"
        disabled={sending}
        className="flex-1 inline-flex items-center justify-center gap-2 bg-ink text-parchment py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-oxblood transition-colors duration-300 disabled:opacity-60"
      >
        {sending ? "Placing order…" : `Place order — $${formatPrice(total)} CAD`}
      </button>
    </div>
  </form>
);

const Success = ({ order, onClose }) => (
  <div data-testid="buy-success" className="mt-6">
    <div className="inline-flex items-center justify-center w-10 h-10 border border-oxblood text-oxblood">
      <Check className="w-4 h-4" />
    </div>
    <p className="mt-4 font-serif text-2xl text-ink">Order received.</p>
    <p className="mt-2 text-sm text-mutedink leading-relaxed">
      {order.quantity} × {order.product_name} · {order.colour} · Size{" "}
      {order.size}
      {order.school_logo && (
        <span data-testid="buy-success-logo">
          {" "}
          · {order.school_name} logo stitched
        </span>
      )}
    </p>
    <p
      data-testid="buy-order-reference"
      className="mt-4 font-mono text-xs tracking-[0.2em] text-oxblood"
    >
      REF {order.reference}
    </p>
    <p className="mt-5 text-sm text-[#2A2A2A] leading-relaxed">
      Online payment is coming soon. We will email{" "}
      <span className="text-ink">{order.customer.email}</span> a secure payment
      link to complete your ${formatPrice(order.total)} CAD order. Nothing has been charged
      yet.
    </p>
    <button
      type="button"
      data-testid="buy-done-btn"
      onClick={onClose}
      className="mt-8 w-full bg-ink text-parchment py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-oxblood transition-colors duration-300"
    >
      Done
    </button>
  </div>
);

export const BuyDialog = ({
  product,
  open,
  onOpenChange,
  stock,
  refreshStock,
}) => {
  const [step, setStep] = useState("options");
  const [size, setSize] = useState("");
  const [colour, setColour] = useState(product.colours[0]);
  const [qty, setQty] = useState(1);
  const [form, setForm] = useState(EMPTY_FORM);
  const [sending, setSending] = useState(false);
  const [order, setOrder] = useState(null);
  const [logo, setLogo] = useState(EMPTY_LOGO);
  const logoEligible = Boolean(product.logoOption);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const total = product.price * qty;

  const reset = () => {
    setStep("options");
    setSize("");
    setColour(product.colours[0]);
    setQty(1);
    setForm(EMPTY_FORM);
    setOrder(null);
    setLogo(EMPTY_LOGO);
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(reset, 300);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (logoEligible && logo.enabled && !logo.file) {
      toast.error("Please upload your school logo", {
        description: "Or untick the school-logo option to order a plain polo.",
      });
      return;
    }
    setSending(true);
    try {
      const { name, email, phone, ...shipping } = form;
      const withLogo = logoEligible && logo.enabled;
      const { data } = await axios.post(`${API}/orders`, {
        product_id: product.id,
        size,
        colour,
        quantity: qty,
        customer: { name, email, phone: phone || null },
        shipping: {
          ...shipping,
          address_line2: shipping.address_line2 || null,
        },
        school_logo: withLogo,
        school_name: withLogo ? logo.schoolName : null,
        logo_path: withLogo ? logo.file.logo_path : null,
        logo_filename: withLogo ? logo.file.logo_filename : null,
        logo_content_type: withLogo ? logo.file.logo_content_type : null,
        liability_accepted: withLogo ? logo.accepted : false,
      });
      setOrder(data);
      setStep("success");
      refreshStock?.();
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error("Just sold out", {
          description: "That size was taken a moment ago. Please pick another.",
        });
        refreshStock?.();
        setSize("");
        setQty(1);
        setStep("options");
      } else {
        toast.error("Could not place your order", {
          description: "Please try again in a moment.",
        });
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => (o ? onOpenChange(true) : close())}
    >
      <DialogContent
        data-testid="buy-dialog"
        className="max-w-lg max-h-[90vh] overflow-y-auto bg-parchment border-ink/10 rounded-none p-8"
      >
        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-mutedink">
          {step === "options"
            ? "Shop"
            : step === "details"
              ? "Delivery details"
              : "Thank you"}
        </p>
        <DialogTitle className="mt-1 font-serif font-light text-2xl text-ink flex items-baseline justify-between gap-4">
          <span>{product.name}</span>
          <span
            data-testid="buy-price"
            className="font-mono text-sm text-oxblood"
          >
            ${formatPrice(product.price)} CAD
          </span>
        </DialogTitle>
        {step === "options" && (
          <Options
            product={product}
            stock={stock}
            size={size}
            setSize={setSize}
            colour={colour}
            setColour={setColour}
            qty={qty}
            setQty={setQty}
            onNext={() => setStep("details")}
          />
        )}
        {step === "details" && (
          <Details
            form={form}
            set={set}
            sending={sending}
            total={total}
            onBack={() => setStep("options")}
            onSubmit={submit}
            logo={logoEligible ? logo : null}
            setLogo={setLogo}
          />
        )}
        {step === "success" && <Success order={order} onClose={close} />}
      </DialogContent>
    </Dialog>
  );
};
